import { useTranslation } from 'i18n';
import { formatNumber, Stack } from 'ui';

import { formatPercentageFeature } from '../../utils/formatFeature/formatFeature';
import { MaterialBreakdownItem } from '../MaterialBreakdownItem/MaterialBreakdownItem';
import { MaterialSampleBreakdownItem } from '../MaterialSampleBreakdownItem/MaterialSampleBreakdownItem';

const PET_SUM_KEYS: string[] = [
  'PET Bottle - Clear',
  'PET Bottle - Clear (Large)',
  'PET Bottle - Clear (Full Sleeve)',
  'PET Bottle - Light Blue',
  'PET Bottle - Dark Blue',
  'PET Bottle - Green',
  'PET Bottle - Other',
  'Non Food Grade PET Bottle',
];

const NON_PET_SUM_KEYS: string[] = ['Clear PET Tray', 'Film', 'Other Plastic', 'Non Plastic', 'Prohibitive', 'Liquid'];

export const PetMaterialSampleBreakdown = ({
  totalWeight,
  breakdown,
  precision = 1,
}: {
  totalWeight: number;
  breakdown: Record<string, number>;
  precision?: number;
}) => {
  const { t } = useTranslation('features');
  const totalPET = PET_SUM_KEYS.reduce((acc, key) => acc + (breakdown[key] ?? 0), 0);
  const totalNonPET = NON_PET_SUM_KEYS.reduce((acc, key) => acc + (breakdown[key] ?? 0), 0);

  return (
    <Stack
      data-testid="pet-material-sample-breakdown"
      sx={{
        gap: 2,
      }}
    >
      <div>
        <MaterialBreakdownItem
          label={t('breakdowns.petMaterialSampleBreakdown.labels.petBottle')}
          variant="bold"
          value={formatNumber(totalPET, {
            maximumFractionDigits: precision,
            minimumFractionDigits: precision,
            style: 'kilogram',
          })}
          secondaryValue={totalPET > 0 ? formatPercentageFeature(totalPET / totalWeight) : '0%'}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={(breakdown['PET Bottle - Clear'] ?? 0) + (breakdown['PET Bottle - Clear (Full Sleeve)'] ?? 0)}
          label={t('breakdowns.petMaterialSampleBreakdown.labels.clear')}
          totalWeight={totalWeight}
        />

        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['PET Bottle - Light Blue']}
          label={t('breakdowns.petMaterialSampleBreakdown.labels.lightBlue')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['PET Bottle - Dark Blue']}
          label={t('breakdowns.petMaterialSampleBreakdown.labels.darkBlue')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['PET Bottle - Green']}
          label={t('breakdowns.petMaterialSampleBreakdown.labels.green')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['PET Bottle - Other']}
          label={t('breakdowns.petMaterialSampleBreakdown.labels.other')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Non Food Grade PET Bottle']}
          label={t('breakdowns.petMaterialSampleBreakdown.labels.clearNonFoodGrade')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          label={t('breakdowns.petMaterialSampleBreakdown.labels.largeClear5L')}
          value={breakdown['PET Bottle - Clear (Large)']}
          totalWeight={totalWeight}
        />
      </div>
      <div>
        <MaterialBreakdownItem
          label={t('breakdowns.petMaterialSampleBreakdown.labels.nonPetBottle')}
          variant="bold"
          value={formatNumber(totalNonPET, {
            maximumFractionDigits: precision,
            minimumFractionDigits: precision,
            style: 'kilogram',
          })}
          secondaryValue={totalNonPET > 0 ? formatPercentageFeature(totalNonPET / totalWeight) : '0%'}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Clear PET Tray']}
          label={t('breakdowns.petMaterialSampleBreakdown.labels.clearPetTray')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown.Film}
          label={t('breakdowns.petMaterialSampleBreakdown.labels.film')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Other Plastic']}
          label={t('breakdowns.petMaterialSampleBreakdown.labels.otherPlastic')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Non Plastic']}
          label={t('breakdowns.petMaterialSampleBreakdown.labels.nonPlastic')}
          totalWeight={totalWeight}
        />

        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown.Prohibitive}
          label={t('breakdowns.petMaterialSampleBreakdown.labels.prohibitive')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown.Liquid}
          label={t('breakdowns.petMaterialSampleBreakdown.labels.liquid')}
          totalWeight={totalWeight}
        />
      </div>
      <MaterialBreakdownItem
        label={t('breakdowns.petMaterialSampleBreakdown.labels.total')}
        value={formatNumber(totalWeight, {
          maximumFractionDigits: precision,
          minimumFractionDigits: precision,
          style: 'kilogram',
        })}
        secondaryValue={'100%'}
        variant="bold"
      />
    </Stack>
  );
};
